<template>
  <v-dialog v-model="dialog"
      persistent
      scrollable
      :fullscreen="$vuetify.display.xs"
      :width="$vuetify.display.smAndUp ? 400 : 'auto'"
      style="z-index: 1100 !important;">

    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ props }" data-cy="authenticity-check-actions-----" />
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span>
          {{ showFullScanWarning
            ? $t('routes.pod.authenticity.fullScanConfirmTitle')
            : $t('routes.pod.authenticity.confirmTitle') }}
        </span>
        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Selection view -->
      <template v-if="!showFullScanWarning">
        <v-card-text>
          <!-- Detection model selection -->
          <div class="mb-3">
            <div class="text-subtitle-2 mb-2">
              {{ $t('routes.pod.authenticity.modelSelection') }}
            </div>
            <v-radio-group v-model="selectedModel" density="compact" hide-details>
              <v-radio :label="genaiRadioLabel" value="genai"
                :disabled="isModelDisabled('genai')" />
              <v-radio :label="deepfakeRadioLabel" value="deepfake"
                :disabled="isModelDisabled('deepfake')" />
            </v-radio-group>

            <v-alert v-if="showModelWarning"
              type="warning" variant="tonal" density="compact" class="mt-2">
              {{ $t('routes.pod.authenticity.modelAlreadyChecked', { model: selectedModelLabel }) }}
            </v-alert>
          </div>

          <v-divider class="mb-3" />

          <!-- Segment selection for video files -->
          <div v-if="isVideo" class="mt-3">
            <div class="text-subtitle-2 mb-2">
              {{ $t('routes.pod.authenticity.segmentSelection') }}
            </div>
            <v-radio-group v-model="rangeMode" density="compact" hide-details class="mb-2">
              <v-radio :label="$t('routes.pod.authenticity.fullVideo')" value="full" :disabled="!canAffordFullScan" />
              <v-radio :label="$t('routes.pod.authenticity.customRange')" value="custom" />
            </v-radio-group>

            <!-- Short video (< 60s): seconds-only fields -->
            <div v-if="rangeMode === 'custom' && !useMinutesFormat">
              <div class="d-flex ga-3 mt-2">
                <v-text-field
                  v-model.number="startSecond"
                  type="number"
                  @keydown="filterNumericInput"
                  :label="$t('routes.pod.authenticity.startTime')"
                  :min="0"
                  :max="flooredDuration"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :error="hasSegmentErrors"
                  class="flex-grow-1"
                />
                <v-text-field
                  v-model.number="endSecond"
                  type="number"
                  @keydown="filterNumericInput"
                  :label="$t('routes.pod.authenticity.endTime')"
                  :min="0"
                  :max="flooredDuration"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :error="hasSegmentErrors"
                  class="flex-grow-1"
                />
              </div>
              <div v-if="segmentErrors.length" class="text-error text-caption mt-1">
                {{ segmentErrors[0] }}
              </div>
            </div>

            <!-- Long video (>= 60s): minutes + seconds fields -->
            <div v-if="rangeMode === 'custom' && useMinutesFormat" class="mt-2">
              <div class="text-caption mb-1">{{ $t('routes.pod.authenticity.startTimeMinSec') }}</div>
              <div class="d-flex ga-3">
                <v-text-field
                  v-model.number="startMinutes"
                  type="number"
                  @keydown="filterNumericInput"
                  :label="$t('routes.pod.authenticity.minutesLabel')"
                  :min="0"
                  :max="Math.floor(flooredDuration / 60)"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :error="hasSegmentErrors"
                  class="flex-grow-1"
                />
                <v-text-field
                  v-model.number="startSeconds"
                  type="number"
                  @keydown="filterNumericInput"
                  :label="$t('routes.pod.authenticity.secondsLabel')"
                  :min="0"
                  :max="59"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :error="hasSegmentErrors"
                  class="flex-grow-1"
                />
              </div>
              <div v-if="segmentErrors.length" class="text-error text-caption mt-1">
                {{ segmentErrors[0] }}
              </div>
              <div class="text-caption mb-1 mt-3">{{ $t('routes.pod.authenticity.endTimeMinSec') }}</div>
              <div class="d-flex ga-3">
                <v-text-field
                  v-model.number="endMinutes"
                  type="number"
                  @keydown="filterNumericInput"
                  :label="$t('routes.pod.authenticity.minutesLabel')"
                  :min="0"
                  :max="Math.floor(flooredDuration / 60)"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :error="hasSegmentErrors"
                  class="flex-grow-1"
                />
                <v-text-field
                  v-model.number="endSeconds"
                  type="number"
                  @keydown="filterNumericInput"
                  :label="$t('routes.pod.authenticity.secondsLabel')"
                  :min="0"
                  :max="59"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :error="hasSegmentErrors"
                  class="flex-grow-1"
                />
              </div>
            </div>
          </div>

          <v-sheet rounded="lg" class="credits-sheet pa-3 mt-3">
            <div v-if="displayCost > 0" class="text-body-1 font-weight-medium">
              {{ $t('routes.pod.authenticity.creditsToDeduct', { credits: displayCost }) }}
            </div>
            <div v-if="available != null" class="text-body-1 text-medium-emphasis mt-1">
              {{ $t('routes.pod.authenticity.creditsAvailable', { credits: available }) }}
            </div>
            <div v-if="isVideo && maxAffordableMinutes != null" class="text-body-1 text-medium-emphasis mt-1">
              {{ $t('routes.pod.authenticity.maxAffordableTime', { minutes: maxAffordableMinutes }) }}
            </div>
          </v-sheet>

        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" variant="flat" :loading="loading" :disabled="hasSegmentErrors || !canAffordCurrentSelection || isSelectedModelCompleted || isSelectedModelPending" @click="confirm">
            {{ $t('routes.pod.authenticity.runCheck') }}
          </v-btn>

          <v-btn variant="text" @click="cancel">
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </template>

      <!-- Full scan warning view -->
      <template v-else>
        <v-card-text>
          <div class="d-flex align-center mb-3">
            <v-icon color="warning" size="large" class="mr-2">mdi-alert-outline</v-icon>
            <p class="mb-0">{{ $t('routes.pod.authenticity.fullScanConfirmMessage') }}</p>
          </div>

          <v-sheet rounded="lg" class="credits-sheet pa-3 mt-3">
            <div v-if="displayCost > 0" class="text-body-1 font-weight-medium">
              {{ $t('routes.pod.authenticity.creditsToDeduct', { credits: displayCost }) }}
            </div>
            <div v-if="available != null" class="text-body-1 text-medium-emphasis mt-1">
              {{ $t('routes.pod.authenticity.creditsAvailable', { credits: available }) }}
            </div>
            <div v-if="isVideo && maxAffordableMinutes != null" class="text-body-1 text-medium-emphasis mt-1">
              {{ $t('routes.pod.authenticity.maxAffordableTime', { minutes: maxAffordableMinutes }) }}
            </div>
          </v-sheet>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" variant="flat" :loading="loading" :disabled="!canAffordFullScan || isSelectedModelPending" @click="confirmFullScan">
            {{ $t('routes.pod.authenticity.runCheck') }}
          </v-btn>

          <v-btn variant="text" @click="goBackFromWarning">
            {{ $t('routes.pod.authenticity.fullScanConfirmBack') }}
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [DialogActionKeys],
  name: 'AuthenticityCheckDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    cost: {
      type: Number,
      default: 0,
    },
    available: {
      type: Number,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isVideo: {
      type: Boolean,
      default: false,
    },
    videoDuration: {
      type: Number,
      default: 0,
    },
    fullVideoCost: {
      type: Number,
      default: 0,
    },
    maxAffordableMinutes: {
      type: Number,
      default: null,
    },
    creditsPerMinute: {
      type: Number,
      default: 100,
    },
    completedModels: {
      type: Array,
      default: () => [],
    },
    pendingModels: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'confirm', 'cancel'],
  data() {
    const floored = Math.floor(this.videoDuration) || 0;
    return {
      selectedModel: 'genai',
      rangeMode: 'full',
      showFullScanWarning: false,
      startSecond: 0,
      endSecond: floored,
      startMinutes: 0,
      startSeconds: 0,
      endMinutes: Math.floor(floored / 60),
      endSeconds: floored % 60,
    };
  },
  watch: {
    videoDuration(val) {
      const floored = Math.floor(val) || 0;
      this.endSecond = floored;
      this.endMinutes = Math.floor(floored / 60);
      this.endSeconds = floored % 60;
    },
    modelValue(val) {
      if (val) {
        const floored = Math.floor(this.videoDuration) || 0;
        const cantAffordFull = this.isVideo && this.available != null && this.fullVideoCost > this.available;
        this.rangeMode = cantAffordFull ? 'custom' : 'full';
        if (!this.isVideo && this.completedModels.length > 0) {
          const available = ['genai', 'deepfake'].filter((m) => !this.completedModels.includes(m));
          this.selectedModel = available.length > 0 ? available[0] : 'genai';
        } else if (this.isVideo && this.pendingModels.length > 0) {
          const unavailable = new Set([...this.pendingModels]);
          const available = ['genai', 'deepfake'].filter((m) => !unavailable.has(m));
          this.selectedModel = available.length > 0 ? available[0] : 'genai';
        } else {
          this.selectedModel = 'genai';
        }
        this.showFullScanWarning = false;
        this.startSecond = 0;

        let endSec = floored;
        if (cantAffordFull && this.maxAffordableMinutes != null && this.maxAffordableMinutes > 0) {
          endSec = Math.min(floored, this.maxAffordableMinutes * 60);
        }

        this.endSecond = endSec;
        this.startMinutes = 0;
        this.startSeconds = 0;
        this.endMinutes = Math.floor(endSec / 60);
        this.endSeconds = endSec % 60;
      }
    },
    startMinutes() {
      this.startSecond = (this.startMinutes * 60) + this.startSeconds;
    },
    startSeconds() {
      this.startSecond = (this.startMinutes * 60) + this.startSeconds;
    },
    endMinutes() {
      this.endSecond = (this.endMinutes * 60) + this.endSeconds;
    },
    endSeconds() {
      this.endSecond = (this.endMinutes * 60) + this.endSeconds;
    },
    completedModels(newVal) {
      if (!this.isVideo && this.modelValue) {
        const available = ['genai', 'deepfake'].filter((m) => !newVal.includes(m));
        if (available.length > 0 && newVal.includes(this.selectedModel)) {
          this.selectedModel = available[0];
        }
      }
    },
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    flooredDuration() {
      return Math.floor(this.videoDuration) || 0;
    },
    useMinutesFormat() {
      return this.videoDuration >= 60;
    },
    customRangeCost() {
      if (this.startSecond == null || this.endSecond == null) return 0;
      const duration = this.endSecond - this.startSecond;
      if (duration <= 0) return 0;
      const minutes = Math.max(1, Math.ceil(duration / 60));
      return minutes * 100;
    },
    displayCost() {
      if (!this.isVideo || this.rangeMode === 'full') {
        return this.cost;
      }
      return this.customRangeCost;
    },
    remainingCredits() {
      if (this.available != null && this.cost > 0) {
        return this.available - this.cost;
      }
      return this.available;
    },
    displayRemainingCredits() {
      if (this.available != null && this.displayCost > 0) {
        return this.available - this.displayCost;
      }
      return this.available;
    },
    segmentErrors() {
      if (!this.isVideo || this.rangeMode !== 'custom') return [];

      const errors = [];

      // Check for non-numeric input (NaN from v-model.number)
      if (typeof this.startSecond !== 'number' || Number.isNaN(this.startSecond)
       || typeof this.endSecond !== 'number' || Number.isNaN(this.endSecond)) {
        errors.push(this.$t('routes.pod.authenticity.invalidTimeValue'));
        return errors;
      }
      // For minutes format, also check the sub-fields
      if (this.useMinutesFormat) {
        if (typeof this.startMinutes !== 'number' || Number.isNaN(this.startMinutes)
         || typeof this.startSeconds !== 'number' || Number.isNaN(this.startSeconds)
         || typeof this.endMinutes !== 'number' || Number.isNaN(this.endMinutes)
         || typeof this.endSeconds !== 'number' || Number.isNaN(this.endSeconds)) {
          errors.push(this.$t('routes.pod.authenticity.invalidTimeValue'));
          return errors;
        }
      }

      if (this.startSecond >= this.endSecond) {
        errors.push(this.$t('routes.pod.authenticity.segmentValidationError'));
      }
      if (this.startSecond < 0 || this.endSecond > this.flooredDuration) {
        errors.push(this.$t('routes.pod.authenticity.segmentOutOfBounds'));
      }
      return errors;
    },
    hasSegmentErrors() {
      return this.segmentErrors.length > 0;
    },
    canAffordFullScan() {
      if (!this.isVideo || this.available == null) return true;
      return this.fullVideoCost <= this.available;
    },
    canAffordCurrentSelection() {
      if (this.available == null) return true;
      return this.displayCost <= this.available;
    },
    genaiRadioLabel() {
      const base = this.$t('routes.pod.authenticity.modelGenai');
      if (!this.isVideo && this.completedModels.includes('genai')) {
        return `${base} (${this.$t('routes.pod.authenticity.modelCompleted')})`;
      }
      if (this.isVideo && this.pendingModels.includes('genai')) {
        return `${base} (${this.$t('routes.pod.authenticity.modelInProgress')})`;
      }
      return base;
    },
    deepfakeRadioLabel() {
      const base = this.$t('routes.pod.authenticity.modelDeepfake');
      if (!this.isVideo && this.completedModels.includes('deepfake')) {
        return `${base} (${this.$t('routes.pod.authenticity.modelCompleted')})`;
      }
      if (this.isVideo && this.pendingModels.includes('deepfake')) {
        return `${base} (${this.$t('routes.pod.authenticity.modelInProgress')})`;
      }
      return base;
    },
    showModelWarning() {
      if (!this.isVideo) return false;
      if (this.rangeMode === 'custom') return false;
      return this.completedModels.includes(this.selectedModel);
    },
    selectedModelLabel() {
      return this.selectedModel === 'genai'
        ? this.$t('routes.pod.authenticity.modelGenai')
        : this.$t('routes.pod.authenticity.modelDeepfake');
    },
    isSelectedModelCompleted() {
      if (this.isVideo) return false;
      return this.completedModels.includes(this.selectedModel);
    },
    isSelectedModelPending() {
      if (!this.isVideo) return false;
      return this.pendingModels.includes(this.selectedModel);
    },
  },
  methods: {
    isModelDisabled(model) {
      if (!this.isVideo && this.completedModels.includes(model)) return true;
      if (this.isVideo && this.pendingModels.includes(model)) return true;
      return false;
    },
    filterNumericInput(event) {
      if (['e', 'E', '+', '-', '.'].includes(event.key)) {
        event.preventDefault();
      }
    },
    confirm() {
      if (this.isVideo && this.rangeMode === 'full') {
        this.showFullScanWarning = true;
        return;
      }
      const data = { model: this.selectedModel };
      if (this.isVideo && this.rangeMode === 'custom') {
        data.startSecond = this.startSecond;
        data.endSecond = this.endSecond;
        data.rangeMode = 'custom';
      } else {
        data.rangeMode = 'full';
      }
      this.$emit('confirm', data);
    },
    confirmFullScan() {
      this.$emit('confirm', { rangeMode: 'full', model: this.selectedModel });
    },
    goBackFromWarning() {
      this.showFullScanWarning = false;
    },
    cancel() {
      this.$emit('cancel');
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
.credits-sheet {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
}
</style>
